<template>
  <div>
    <h3 class="font-semibold leading-6 mb-4 text-init mt-0">
      Facilities
    </h3>
    <BaseChipSelector
      v-model="roomFeaturesSelected"
      class="flex-wrap mb-3"
      :options="visibleRoomFeatures"
      @change="updateRoomFeatures"
    />
    <a
      v-show="visibleRoomFeatures.length <= 4"
      class="font-semibold cursor-pointer"
      @click="showingAllRoomFeatures = true"
    >
      + Show all
      <span v-if="hiddenSelectedElements">({{ hiddenSelectedElements }})</span>
    </a>
  </div>
</template>

<script setup>
import AirConditioning from '@tagvenue/icons/16/room-features/air-conditioning.svg';
import Backdrops from '@tagvenue/icons/16/room-features/backdrop-01.svg';
import PhotoEquipment from '@tagvenue/icons/16/room-features/camera-01.svg';
import ConferenceCallFacilities from '@tagvenue/icons/16/room-features/conference-call-facilities.svg';
import DanceFloor from '@tagvenue/icons/16/room-features/discoball.svg';
import FlatscreenTv from '@tagvenue/icons/16/room-features/flatscreen-tv.svg';
import Flipchart from '@tagvenue/icons/16/room-features/flipchart.svg';
import Greenscreen from '@tagvenue/icons/16/room-features/greenscreen-01.svg';
import LightningEquipment from '@tagvenue/icons/16/room-features/lightbulb-04.svg';
import Mirrors from '@tagvenue/icons/16/room-features/mirror-01.svg';
import NaturalLight from '@tagvenue/icons/16/room-features/natural-light.svg';
import PaSystem from '@tagvenue/icons/16/room-features/pa-system.svg';
import Projector from '@tagvenue/icons/16/room-features/projector.svg';
import QuietSpace from '@tagvenue/icons/16/room-features/quiet_space.svg';
import Soundproof from '@tagvenue/icons/16/room-features/soundproof.svg';
import StorageSpace from '@tagvenue/icons/16/room-features/storage-space.svg';
import VideoEquipment from '@tagvenue/icons/16/room-features/video-recorder.svg';
import Whiteboard from '@tagvenue/icons/16/room-features/whiteboard.svg';
import WifiIcon from '@tagvenue/icons/24/line/media-devices/wifi.svg';
import { computed, markRaw, ref, watch } from 'vue';
import { useStore } from 'vuex';

import BaseChipSelector from '@/components/Ui/Forms/BaseChipSelector.vue';

// Props & Emits
const props = defineProps({
  roomFeatures: {
    type: Array,
    required: true,
  },
  selectedRoomFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['change']);

// Reactive state
const showingAllRoomFeatures = ref(false);
const roomFeaturesSelected = ref([]);

// order per category
const store = useStore();

const limit = 4;

const order = {
  meeting: [5, 8, 9, 10, 19, 13, 6, 12, 11, 7, 16, 26, 27, 21, 22, 28, 23, 24, 25],
  business: [5, 8, 12, 9, 19, 6, 7, 13, 10, 11, 16, 26, 27, 21, 22, 28, 23, 24, 25],
  venue_type: [12, 5, 8, 19, 7, 6, 9, 13, 10, 16, 11, 25, 26, 21, 22, 28, 23, 24, 27],
  party: [12, 5, 8, 7, 19, 9, 6, 10, 16, 13, 11, 25, 26, 27, 21, 22, 28, 23, 24],
  wedding: [12, 5, 6, 7, 19, 8, 16, 10, 11, 13, 21, 22, 28, 27, 26, 23, 24, 25],
  production: [12, 5, 19, 6, 8, 7, 10, 9, 13, 16, 21, 22, 28, 23, 24, 26, 27, 25, 11],
};

// Icons mapping
const roomFacilitiesIcons = {
  5: markRaw(WifiIcon),
  6: markRaw(NaturalLight),
  7: markRaw(AirConditioning),
  8: markRaw(Projector),
  9: markRaw(FlatscreenTv),
  10: markRaw(Whiteboard),
  11: markRaw(Flipchart),
  12: markRaw(PaSystem),
  13: markRaw(ConferenceCallFacilities),
  16: markRaw(StorageSpace),
  19: markRaw(QuietSpace),
  21: markRaw(PhotoEquipment),
  22: markRaw(VideoEquipment),
  23: markRaw(Greenscreen),
  24: markRaw(Backdrops),
  25: markRaw(DanceFloor),
  26: markRaw(Soundproof),
  27: markRaw(Mirrors),
  28: markRaw(LightningEquipment),
};

const roomFeatures = computed(() => {
  const list = [...props.roomFeatures].map((option) => ({
    value: option.id,
    id: parseInt(option.id, 10),
    label: option.label,
    iconComponent: roomFacilitiesIcons[option.id],
  }));

  const orderForCategory = order[store.state.eventCategory];
  return list.filter((item) => orderForCategory.includes(item.id)).sort((a, b) => {
    return orderForCategory.indexOf(a.id) - orderForCategory.indexOf(b.id);
  });
});

const visibleRoomFeatures = computed(() => {
  if (showingAllRoomFeatures.value) {
    return roomFeatures.value;
  }
  return roomFeatures.value.slice(0, limit);
});

// Methods
const updateRoomFeatures = (key, value) => {
  emit('change', key, value);
};

const hiddenSelectedElements = computed(() => {
  const options = roomFeatures.value.slice(limit);
  return options.reduce((acc, option) => acc + (props.selectedRoomFeatures[option.id] ? 1 : 0), 0);
});

watch(() => props.selectedRoomFeatures, () => {
  roomFeaturesSelected.value = Object.keys(props.selectedRoomFeatures).filter((key) => props.selectedRoomFeatures[key]);
}, { immediate: true });
</script>
